import { FieldValues, useController } from 'react-hook-form';
import { Field, FieldProps as SWFieldProps } from '@spark-web/field';
import { RadioGroup } from '@spark-web/radio';
import { RadioGroupProps } from '@spark-web/radio/dist/declarations/src/types';
import { FormFieldProps } from '../Form';

type StringBooleanType = string | boolean;

export type RadioGroupFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  Value extends StringBooleanType = StringBooleanType,
> = FormFieldProps<TFieldValues> &
  Omit<RadioGroupProps<Value>, 'onChange' | 'value'> & {
    FieldProps?: Omit<SWFieldProps, keyof RadioGroupFieldProps>;
    /** Function that is fired whenever a change event is triggered on a `Radio`. */
    onChange?: (selectedValue: Value) => void;
    /** The value of the nested radios. */
    value?: Value;
  };

function RadioGroupField<
  TFieldValues extends FieldValues = FieldValues,
  Value extends StringBooleanType = StringBooleanType,
>({
  name,
  label,
  tone,
  message,
  control,
  children,
  FieldProps,
  shouldUnregister,
  ...radioGroupFieldProps
}: RadioGroupFieldProps<TFieldValues, Value>) {
  const {
    field: { ref, onChange, ...field },
    fieldState: { error },
  } = useController<TFieldValues>({
    name,
    control,
    shouldUnregister,
  });

  const handleChange = (selectedValue: Value) => {
    onChange(selectedValue);

    radioGroupFieldProps.onChange?.(selectedValue);
  };

  return (
    <Field label={label} {...FieldProps}>
      <RadioGroup<Value>
        {...radioGroupFieldProps}
        {...field}
        tone={error ? 'critical' : tone}
        message={error?.message ?? message}
        onChange={handleChange}
      >
        {children}
      </RadioGroup>
    </Field>
  );
}

export default RadioGroupField;
